import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { TruckIcon, XIcon, PrinterIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

/** Guía de remisión desde el catálogo de clientes. La factura es opcional: un cliente sin facturas
 *  también puede imprimir su guía. Se elige la dirección de envío (principal o una registrada). */
export default function ClientGuideModal({ cliente, onClose }) {
  const [facturas, setFacturas] = useState(null);
  const [destinos, setDestinos] = useState([]);
  const [factura, setFactura] = useState("");
  const [destino, setDestino] = useState("");

  useEffect(() => {
    api.get("/invoices", { params: { cliente_id: cliente.id, tipo: "factura" } })
      .then(({ data }) => {
        const list = (data || []).filter((f) => f.estado !== "anulada" && f.tipo !== "proforma")
          .sort((a, b) => String(b.fecha).localeCompare(String(a.fecha)));
        setFacturas(list);
        if (list.length) { setFactura(list[0].id); setDestino(list[0].destino_envio_id || ""); }
      }).catch((e) => { toast.error(apiErr(e)); setFacturas([]); });
    api.get(`/clients/${cliente.id}/shipping-destinations`).then(({ data }) => setDestinos(data || [])).catch(() => setDestinos([]));
  }, [cliente.id]);

  const [busy, setBusy] = useState(false);
  const imprimir = async () => {
    const q = destino ? `?destino_id=${encodeURIComponent(destino)}` : "";
    if (factura) {
      window.open(`/ventas/${factura}/guia${q}`, "_blank");
      onClose();
      return;
    }
    const w = window.open("about:blank", "_blank");   // se abre antes de esperar (evita el bloqueo de ventanas)
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${cliente.id}/guia-remision`, { destino_id: destino || null });
      const url = `/ventas/${data.id}/guia${q}`;
      if (w) w.location.href = url; else window.open(url, "_blank");
      onClose();
    } catch (e) { if (w) w.close(); toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const dir = (d) => [d.direccion, d.ciudad, d.departamento].filter(Boolean).join(", ");
  const vm = useModalForm({ onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...vm.overlayProps}>
      <div className="bg-card border border-blue-500/40 rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}
        data-testid="client-guide-modal">
        <div className="p-5 border-b border-border/50 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/15 flex items-center justify-center"><TruckIcon size={22} weight="fill" className="text-blue-400" /></div>
            <div>
              <h3 className="text-lg font-bold">Guía de remisión</h3>
              <p className="text-xs text-text-muted">{cliente.nombre}</p>
            </div>
          </div>
          <button aria-label="Cerrar" onClick={vm.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          {facturas === null ? <div className="text-sm text-text-muted">Cargando…</div> : (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">Factura relacionada (opcional)</label>
                <select value={factura} onChange={(e) => { setFactura(e.target.value); const f = facturas.find((x) => x.id === e.target.value); if (f) setDestino(f.destino_envio_id || ""); }}
                  data-testid="guide-factura" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
                  <option value="">Sin factura relacionada</option>
                  {facturas.map((f) => <option key={f.id} value={f.id}>{f.numero} · {fmtDate(f.fecha)} · {fmtL(f.total)}</option>)}
                </select>
                {facturas.length === 0 && <div className="text-[11px] text-text-muted mt-1" data-testid="guide-sin-facturas">El cliente no tiene facturas: la guía se emite sin factura relacionada.</div>}
              </div>
              <div data-testid="guide-destinos">
                <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">Dirección de envío</label>
                <div className="space-y-2">
                  <label className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer ${destino === "" ? "border-blue-500 bg-blue-500/5" : "border-border/60"}`}>
                    <input type="radio" name="destino" checked={destino === ""} onChange={() => setDestino("")} className="mt-1 accent-blue-500" data-testid="guide-dest-principal" />
                    <div><div className="text-sm font-medium">Dirección principal del cliente</div>
                      <div className="text-xs text-text-muted">{cliente.direccion || "La registrada en el perfil del cliente"}</div></div>
                  </label>
                  {destinos.map((d) => (
                    <label key={d.id} className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer ${destino === d.id ? "border-blue-500 bg-blue-500/5" : "border-border/60"}`}>
                      <input type="radio" name="destino" checked={destino === d.id} onChange={() => setDestino(d.id)} className="mt-1 accent-blue-500" data-testid={`guide-dest-${d.id}`} />
                      <div><div className="text-sm font-medium">{d.alias || d.nombre || "Destino"}</div>
                        <div className="text-xs text-text-muted">{dir(d) || "Sin dirección"}{d.encargado ? ` · ${d.encargado}` : ""}</div></div>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        <div className="p-5 border-t border-border/50 flex justify-end gap-2">
          <button onClick={vm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
          <button onClick={imprimir} disabled={busy || facturas === null} data-testid="guide-imprimir"
            className="px-4 py-2 text-sm bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-md flex items-center gap-2 disabled:opacity-40">
            <PrinterIcon size={14} /> Imprimir guía
          </button>
        </div>
      </div>
    </div>
  );
}
